import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { UserProfile } from '@/types';
import { cognitoService } from '@/services/cognito';

type AuthContextValue = {
	user: UserProfile | null;
	signIn: (email: string, password: string) => Promise<void>;
	register: (name: string, email: string, password: string, locale?: string) => Promise<void>;
	confirmSignUp: (email: string, code: string) => Promise<void>;
	resendConfirmation: (email: string) => Promise<void>;
	forgotPassword: (email: string) => Promise<void>;
	confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
	changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
	signOut: () => void;
	getIdToken: () => string | null;
	getAuthHeader: () => Record<string, string> | {};
	getUserId: () => string | null;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const TOKEN_KEYS = {
	id: 'cognito.idToken',
	access: 'cognito.accessToken',
	refresh: 'cognito.refreshToken',
	username: 'cognito.username',
	createdAt: 'app.user.createdAt'
} as const;

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
	const [user, setUser] = useState<UserProfile | null>(null);

	const value = useMemo<AuthContextValue>(
		() => ({
			user,
			signIn: async (email, password) => {
				const auth = await cognitoService.signIn(email, password);
				localStorage.setItem(TOKEN_KEYS.id, auth.idToken);
				localStorage.setItem(TOKEN_KEYS.access, auth.accessToken);
				if (auth.refreshToken) localStorage.setItem(TOKEN_KEYS.refresh, auth.refreshToken);
				localStorage.setItem(TOKEN_KEYS.username, email);
				const payload = cognitoService.parseJwt(auth.idToken);
				const createdAt = localStorage.getItem(TOKEN_KEYS.createdAt) || new Date().toISOString();
				localStorage.setItem(TOKEN_KEYS.createdAt, createdAt);
				const groups = (payload?.['cognito:groups'] as string[] | undefined) || [];
				const isAdminFromGroups =
					Array.isArray(groups) &&
					groups.map((g) => String(g).toLowerCase()).includes('admin');

				const isMember = groups.map((g) => String(g).toLowerCase()).includes('member');
				setUser({
					id: payload?.sub || email,
					name: payload?.name || email.split('@')[0],
					email: payload?.email || email,
					isAdmin: isAdminFromGroups,
					isMember: isMember,
					favoriteRecipeIds: [],
					createdAt,
					...(payload?.locale ? { country: payload.locale as string } : {})
				});
			},
			getIdToken: () => localStorage.getItem(TOKEN_KEYS.id),
			getAuthHeader: () => {
				const t = localStorage.getItem(TOKEN_KEYS.id);
				return t ? { Authorization: `Bearer ${t}` } : {};
			},
			getUserId: () => {
				const idToken = localStorage.getItem(TOKEN_KEYS.id);
				if (!idToken) return null;
				try {
					const payload = cognitoService.parseJwt(idToken);
					// Prefer cognito:username, fallback to sub
					return (payload?.['cognito:username'] as string | undefined) || (payload?.sub as string | undefined) || null;
				} catch (error) {
					console.error('Error parsing JWT for userId:', error);
					return null;
				}
			},
			register: async (name, email, password, locale?: string) => {
				await cognitoService.signUp(email, password, name, locale);
			},
			confirmSignUp: async (email, code) => {
				await cognitoService.confirmSignUp(email, code);
			},
			resendConfirmation: async (email) => {
				await cognitoService.resendConfirmation(email);
			},
			forgotPassword: async (email) => {
				await cognitoService.forgotPassword(email);
			},
			confirmForgotPassword: async (email, code, newPassword) => {
				await cognitoService.confirmForgotPassword(email, code, newPassword);
			},
			changePassword: async (currentPassword, newPassword) => {
				const idToken = localStorage.getItem(TOKEN_KEYS.id);
				const payload = idToken ? cognitoService.parseJwt(idToken) : null;
				const expSec = payload?.exp;
				if (expSec && Date.now() > expSec * 1000) {
					await refreshTokenIfExpired();
				}
				const access = localStorage.getItem(TOKEN_KEYS.access);
				if (!access) {
					throw new Error('Sessão expirada. Faça login novamente.');
				}
				await cognitoService.changePassword(access, currentPassword, newPassword);
			},
			signOut: () => {
				const access = localStorage.getItem(TOKEN_KEYS.access);
				if (access) {
					cognitoService.globalSignOut(access).catch(() => {});
				}
				localStorage.removeItem(TOKEN_KEYS.id);
				localStorage.removeItem(TOKEN_KEYS.access);
				localStorage.removeItem(TOKEN_KEYS.refresh);
				localStorage.removeItem(TOKEN_KEYS.username);
				setUser(null);
			}
		}),
		[user]
	);

	async function refreshTokenIfExpired(): Promise<string | null> {
		try {
			const refreshToken = localStorage.getItem(TOKEN_KEYS.refresh);
			if (refreshToken) {
				const storedUsername = localStorage.getItem(TOKEN_KEYS.username) || undefined;
				const idToken = localStorage.getItem(TOKEN_KEYS.id);
				const payload = idToken ? cognitoService.parseJwt(idToken) : null;
				const cognitoUsername = (payload?.['cognito:username'] as string | undefined) || undefined;
				const emailFromToken = (payload?.email as string | undefined) || undefined;
				const username = cognitoUsername || storedUsername || emailFromToken || undefined;
				const auth = await cognitoService.refresh(refreshToken, username);
				localStorage.setItem(TOKEN_KEYS.id, auth.idToken);
				localStorage.setItem(TOKEN_KEYS.access, auth.accessToken);
				if (auth.refreshToken) localStorage.setItem(TOKEN_KEYS.refresh, auth.refreshToken);
				if (!storedUsername && (username || emailFromToken)) {
					localStorage.setItem(TOKEN_KEYS.username, username || emailFromToken!);
				}
				return auth.idToken;
			}
		} catch (error) {
			console.error('Error refreshing token:', error);
		}
		return null;
	}

	useEffect(() => {
		const init = async () => {
			const idToken = localStorage.getItem(TOKEN_KEYS.id);
			const createdAt = localStorage.getItem(TOKEN_KEYS.createdAt) || new Date().toISOString();
			if (idToken) {
				let payload = cognitoService.parseJwt(idToken);
				const expSec = payload?.exp;
				if (expSec && Date.now() > expSec * 1000) {
					const newId = await refreshTokenIfExpired();
					if (newId) {
						payload = cognitoService.parseJwt(newId);
					} else {
						const latestId = localStorage.getItem(TOKEN_KEYS.id);
						if (latestId) {
							payload = cognitoService.parseJwt(latestId);
						}
					}
				}
				if (payload?.sub && payload?.email) {
					const existingUsername = localStorage.getItem(TOKEN_KEYS.username);
					const cognitoUsername = (payload['cognito:username'] as string | undefined) || undefined;
					if (!existingUsername && (cognitoUsername || payload.email)) {
						localStorage.setItem(TOKEN_KEYS.username, cognitoUsername || payload.email);
					}
					const groups = (payload['cognito:groups'] as string[] | undefined) || [];
					const isAdminFromGroups =
						Array.isArray(groups) &&
						groups.map((g) => String(g).toLowerCase()).includes('admin');
					const isMember = groups.map((g) => String(g).toLowerCase()).includes('member');
					setUser({
						id: payload.sub,
						name: payload.name || payload.email.split('@')[0],
						email: payload.email,
						isAdmin: isAdminFromGroups,
						isMember: isMember,
						favoriteRecipeIds: [],
						createdAt,
						...(payload.locale ? { country: payload.locale as string } : {})
					});
				}
			}
		};
		void init();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
	const ctx = useContext(AuthContext);
	if (!ctx) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return ctx;
};
